import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import FaiBox from "../../../MuiComponents/FaiBox";
import FaiTypography from "../../../MuiComponents/FaiTypography";
import theme from "../../../assets/theme";
import { Form, Formik, FormikProps, FormikErrors } from "formik";
import {
	Checkbox,
	FormControlLabel,
	Grid,
	IconButton,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
} from "@mui/material";
import FormField from "../../../components/FormField";
import FaiButton from "../../../MuiComponents/FaiButton";
import FaiSelect from "../../../MuiComponents/FaiSelect";
import { merchantAuthController } from "../../../controllers/authController";
import {
	ProductController,
	ApiResponse,
} from "../../../controllers/productController";
import { AxiosResponse } from "axios";
import { toast } from "react-toastify";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useProfile } from "../../../context/ProfileContext";
import * as Yup from "yup";

interface MetadataField {
	values: string[];
	isRequired: boolean;
}

interface ProductType {
	id: string;
	name: string;
	metadata: {
		[key: string]: MetadataField;
	};
}

interface SelectOption {
	value: string;
	label: string;
}

interface Brand {
	brandName: string;
	logo: string | File | null;
	id?: string; // Add ID for existing brands
}

interface InitialObject {
	brands: Brand[];
	category: SelectOption | null;
	numberOfProducts: string;
	sellOnOtherPlatforms: SelectOption | null;
	selfDeclaration: boolean;
	termsAndCOnditionAggrement: boolean;
	isDraft: boolean;
	metadata: {
		[key: string]: string;
	};
	productType?: ProductType | null;
}

interface BrandFormErrors {
	brands?: {
		[key: number]: {
			brandName?: string;
		};
	};
}

// Validation schema
const BrandDetailsSchema = Yup.object().shape({
	brands: Yup.array()
		.of(
			Yup.object().shape({
				brandName: Yup.string()
					.trim()
					.required("Brand name is required")
					.min(1, "Brand name cannot be empty"),
				logo: Yup.mixed().nullable(),
			})
		)
		.min(1, "At least one brand is required")
		.test(
			"at-least-one-valid-brand",
			"At least one brand must have a name",
			function (value) {
				if (!value) return false;
				return value.some(
					(brand) => brand.brandName && brand.brandName.trim() !== ""
				);
			}
		),
});

function BrandDetails() {
	const navigate = useNavigate();
	const { typography, palette, borders } = theme;
	const { profile, setProfile } = useProfile();
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState(false);
	const [modifiedBrands, setModifiedBrands] = useState<Set<number>>(new Set()); // Track modified brand indices
	const [originalBrands, setOriginalBrands] = useState<Brand[]>([]); // Store original brand data
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [brandToDelete, setBrandToDelete] = useState<{
		name: string;
		index: number;
	} | null>(null);
	const [initialObject, setInitialObject] = useState<InitialObject>({
		brands: [{ brandName: "", logo: null }],
		category: null,
		numberOfProducts: "",
		sellOnOtherPlatforms: null,
		selfDeclaration: false,
		termsAndCOnditionAggrement: false,
		isDraft: false,
		metadata: {},
	});

	// Load existing brands when component mounts
	useEffect(() => {
		const loadExistingBrands = async () => {
			try {
				setLoading(true);
				const response = await merchantAuthController.getBrandList();
				// console.log("Existing brands:", response.data.data.data);

				if (response.data.data.data && response.data.data.data.length > 0) {
					// Transform existing brand data to match our interface
					const existingBrands: Brand[] = response.data.data.data.map(
						(brand: any) => ({
							brandName: brand.brandName || "",
							logo: brand.brandLogo || null, // This will be a URL for existing brands
							id: brand.id || brand._id, // Handle both id and _id
						})
					);

					// Store original brands for comparison
					setOriginalBrands([...existingBrands]);

					setInitialObject((prev) => ({
						...prev,
						brands: existingBrands,
					}));

					// Initialize categoryImages array with existing logos
					const existingImages = existingBrands.map((brand) =>
						brand.logo && typeof brand.logo === "string" ? brand.logo : null
					);
					setCategoryImages(existingImages);

					toast.success(`Loaded ${existingBrands.length} existing brand(s)`);
				} else {
					// No existing brands, keep default empty brand
					setInitialObject((prev) => ({
						...prev,
						brands: [{ brandName: "", logo: null }],
					}));
					setCategoryImages([null]);
					setOriginalBrands([]);
				}
			} catch (err: any) {
				console.error("Error loading existing brands:", err);
				toast.error(
					"Failed to load existing brands. Starting with empty form."
				);
				// Fallback to empty brand if loading fails
				setInitialObject((prev) => ({
					...prev,
					brands: [{ brandName: "", logo: null }],
				}));
				setCategoryImages([null]);
				setOriginalBrands([]);
			} finally {
				setLoading(false);
			}
		};

		loadExistingBrands();
	}, []);

	// Function to check if a brand has a logo
	const hasBrandLogo = (index: number): boolean => {
		const image = categoryImages[index];
		return image instanceof File || (typeof image === "string" && image !== "");
	};

	// Function to check if a brand has been modified
	const isBrandModified = (index: number, currentBrand: Brand): boolean => {
		if (index >= originalBrands.length) {
			// This is a new brand (not in original list)
			return (
				currentBrand.brandName.trim() !== "" ||
				categoryImages[index] instanceof File
			);
		}

		const originalBrand = originalBrands[index];

		// Check if brand name changed
		if (originalBrand.brandName !== currentBrand.brandName) {
			return true;
		}

		// Check if logo changed (new file uploaded or existing logo removed)
		const currentImage = categoryImages[index];
		const originalLogo = originalBrand.logo;

		if (currentImage instanceof File) {
			// New file uploaded
			return true;
		}

		if (originalLogo && !currentImage) {
			// Existing logo was removed
			return true;
		}

		return false;
	};

	// Function to update modified brands tracking
	const updateModifiedBrands = (index: number, currentBrand: Brand) => {
		const isModified = isBrandModified(index, currentBrand);
		setModifiedBrands((prev) => {
			const newSet = new Set(prev);
			if (isModified) {
				newSet.add(index);
			} else {
				newSet.delete(index);
			}
			return newSet;
		});
	};

	const handleSubmit = async (
		values: InitialObject,
		{ setSubmitting }: any
	) => {
		try {
			setLoading(true);
			setError(null);

			// Filter out brands with empty names before submission
			const validBrands = values.brands.filter(
				(brand) => brand.brandName.trim() !== ""
			);

			if (validBrands.length === 0) {
				toast.error("At least one brand with a name is required");
				setError("At least one brand with a name is required");
				return;
			}

			// Create FormData object
			const formData = new FormData();
			formData.append("isDraft", values.isDraft.toString());

			// Only append brands that have been modified or are new
			let brandIndex = 0;
			values.brands.forEach((brand, index) => {
				// const isModified = isBrandModified(index, brand);
				// const hasName = brand.brandName.trim() !== "";
				// const hasLogo = hasBrandLogo(index);

				// Add brand name

				// Add brand ID if it exists (for existing brands)

				// Add brand logo
				if (categoryImages[index] instanceof File) {
					if (brand.id) {
						formData.append(`brands[${brandIndex}].id`, brand.id);
					}
					formData.append(
						`brands[${brandIndex}].brandName`,
						brand.brandName.trim()
					);
					formData.append(
						`brands[${brandIndex}].brandLogo`,
						categoryImages[index]
					);
					brandIndex++;
				} else if (typeof categoryImages[index] === "string") {
					// formData.append(`brands[${index}].brandLogo`, categoryImages[index]);
				}

				// if (isModified && hasName && hasLogo) {
				//     console.log(`Brand ${index} (${brand.brandName}) is modified and has both name and logo, including in submission`);

				//     // Add brand name
				//     formData.append(`brands[${brandIndex}].brandName`, brand.brandName.trim());

				//     // Add brand ID if it exists (for existing brands)
				//     if (brand.id) {
				//         formData.append(`brands[${brandIndex}].id`, brand.id);
				//     }

				//     // Add brand logo
				//     if (categoryImages[index] instanceof File) {
				//         formData.append(`brands[${brandIndex}].brandLogo`, categoryImages[index]);
				//     } else if (typeof categoryImages[index] === 'string') {
				//         formData.append(`brands[${brandIndex}].brandLogo`, categoryImages[index]);
				//     }

				//     brandIndex++;
				// } else {
				//     if (!hasName) {
				//         console.log(`Brand ${index} has no name, skipping`);
				//     } else if (!hasLogo) {
				//         console.log(`Brand ${index} (${brand.brandName}) has no logo, skipping`);
				//     } else {
				//         console.log(`Brand ${index} (${brand.brandName}) is unchanged, skipping`);
				//     }
				// }
			});

			// Check if we have any valid brands to submit
			// if (brandIndex === 0) {
			//     toast.error('No valid brands to submit. Please ensure all brands have names and logos.');
			//     setError('No valid brands to submit. Please ensure all brands have names and logos.');
			//     return;
			// }

			// Log FormData contents for debugging
			// console.log('FormData contents (only modified brands):');
			// Array.from(formData.entries()).forEach(([key, value]) => {
			//     console.log(`${key}: ${value instanceof File ? value.name : value}`);
			// });
			// Use the controller's setBrandDetails method
			const response = await merchantAuthController.setBrandDetails(formData);

			if (response.status === 201 || response.status === 200) {
				// Update the profile context with the new brand details
				const savedBrands = values.brands
					.filter((brand) => brand.brandName.trim() !== "")
					.map((brand, index) => ({
						brandName: brand.brandName,
						brandLogo: categoryImages[index] instanceof File
							? URL.createObjectURL(categoryImages[index] as File)
							: categoryImages[index] || null,
						id: brand.id,
					}));

				setProfile({
					...profile,
					brandDetails: {
						brands: savedBrands,
					},
				});

				setSuccess(true);
				toast.success("Brand details saved successfully!");
				if (!values.isDraft) {
					// Navigate to next step if not draft
					navigate("/app/onboarding/logisticsdetails");
				}
			}
		} catch (err: any) {
			console.error("Error saving brand details:", err);
			const errorMessage =
				err.response?.data?.message ||
				err.response?.data?.errors?.join(", ") ||
				"Failed to save brand details";
			setError(errorMessage);
			toast.error(errorMessage);
		} finally {
			setLoading(false);
			setSubmitting(false);
		}
	};

	const [categoryImages, setCategoryImages] = useState<
		(File | string | null)[]
	>([]);

	// Handle removing a document

	const deleteBrandFromServer = async (brandName: string, index: number) => {
		try {
			setLoading(true);
			const response = await merchantAuthController.deleteBrand(brandName);

			toast.success(`Brand "${brandName}" deleted successfully`);

			// Remove from local state
			const newBrands = initialObject.brands.filter((_, i) => i !== index);
			const newImages = categoryImages.filter((_, i) => i !== index);
			setInitialObject((prev) => ({ ...prev, brands: newBrands }));
			setCategoryImages(newImages);

			// Update original brands list
			setOriginalBrands((prev) => prev.filter((_, i) => i !== index));

			// Update modified brands tracking
			setModifiedBrands((prev) => {
				const newSet = new Set(prev);
				newSet.delete(index);
				// Adjust indices for brands after the removed one
				const adjustedSet = new Set<number>();
				newSet.forEach((i: number) => {
					if (i > index) {
						adjustedSet.add(i - 1);
					} else {
						adjustedSet.add(i);
					}
				});
				return adjustedSet;
			});
		} catch (err: any) {
			console.error("Error deleting brand:", err);
			const errorMessage =
				err.response?.data?.message || "Failed to delete brand";
			toast.error(errorMessage);
		} finally {
			setLoading(false);
			setDeleteDialogOpen(false);
			setBrandToDelete(null);
		}
	};

	const openDeleteDialog = (brandName: string, index: number) => {
		setBrandToDelete({ name: brandName, index });
		setDeleteDialogOpen(true);
	};

	const handleDeleteConfirm = () => {
		if (brandToDelete) {
			deleteBrandFromServer(brandToDelete.name, brandToDelete.index);
		}
	};

	const handleDeleteCancel = () => {
		setDeleteDialogOpen(false);
		setBrandToDelete(null);
	};

	return (
		<>
			<FaiBox
				bgColor={palette.white.main}
				className={"business-details"}
				mt={"15px"}
				p={"15px"}
				borderRadius={borders.borderRadius.md}
			>
				<FaiBox>
					<FaiBox>
						<FaiTypography
							variant="h4"
							fontWeight={typography.fontWeightMedium}
						>
							Brand Details
						</FaiTypography>
						<FaiTypography variant="h6" mb={"21px"}>
							{loading ? "Loading existing brands..." : "Manage your brands"}
						</FaiTypography>
					</FaiBox>
				</FaiBox>

				<Formik<InitialObject>
					initialValues={initialObject}
					enableReinitialize={true}
					validateOnBlur={true}
					validateOnChange={true}
					validationSchema={BrandDetailsSchema}
					onSubmit={handleSubmit}
				>
					{(props: FormikProps<InitialObject>) => {
						const {
							handleSubmit,
							setFieldValue,
							handleChange,
							handleBlur,
							values,
							touched,
							isValid,
							errors,
						} = props;

						const addBrand = () => {
							setFieldValue("brands", [
								...values.brands,
								{ brandName: "", logo: null },
							]);
							setCategoryImages([...categoryImages, null]);
						};

						const removeBrand = (index: number) => {
							const newBrands = values.brands.filter((_, i) => i !== index);
							const newImages = categoryImages.filter((_, i) => i !== index);
							setFieldValue("brands", newBrands);
							setCategoryImages(newImages);

							// Update modified brands tracking
							setModifiedBrands((prev) => {
								const newSet = new Set(prev);
								newSet.delete(index);
								// Adjust indices for brands after the removed one
								const adjustedSet = new Set<number>();
								newSet.forEach((i: number) => {
									if (i > index) {
										adjustedSet.add(i - 1);
									} else {
										adjustedSet.add(i);
									}
								});
								return adjustedSet;
							});
						};

						// Custom change handler to track modifications
						const handleBrandChange = (
							e: React.ChangeEvent<HTMLInputElement>
						) => {
							handleChange(e);
							const fieldName = e.target.name;
							const match = fieldName.match(/brands\.(\d+)\.brandName/);
							if (match) {
								const index = parseInt(match[1]);
								const currentBrand = values.brands[index];
								updateModifiedBrands(index, currentBrand);
							}
						};

						// Custom image change handler to track modifications
						const handleImageChange = (index: number, files: File[]) => {
							const newImages = [...categoryImages];
							// Always take the last file added to ensure we replace the previous one
							// validationSchema handles the requirement check
							const fileToUse = files.length > 0 ? files[files.length - 1] : null;

							newImages[index] = fileToUse;
							setCategoryImages(newImages);
							setFieldValue(`brands.${index}.logo`, fileToUse);

							// Update modified brands tracking
							const currentBrand = values.brands[index];
							updateModifiedBrands(index, currentBrand);
						};

						// Custom image remove handler for existing logos
						const handleExistingImageRemove = (index: number) => {
							const newImages = [...categoryImages];
							newImages[index] = null;
							setCategoryImages(newImages);
							setFieldValue(`brands.${index}.logo`, null);

							// Update modified brands tracking
							const currentBrand = values.brands[index];
							updateModifiedBrands(index, currentBrand);
						};

						return (
							<Form>
								{values.brands.map((brand, index) => {
									const isModified = isBrandModified(index, brand);
									const isNewBrand = index >= originalBrands.length;
									const hasName = brand.brandName.trim() !== "";
									const hasLogo = hasBrandLogo(index);
									const isValid = hasName && hasLogo;

									return (
										<FaiBox
											key={index}
											mb={3}
											p={2}
											border={`1px solid ${isModified
												? isValid
													? palette.primary.main
													: palette.error.main
												: palette.grey[200]
												}`}
											borderRadius={borders.borderRadius.md}
											sx={{
												backgroundColor: isModified
													? isValid
														? "rgba(25, 118, 210, 0.04)"
														: "rgba(211, 47, 47, 0.04)"
													: "transparent",
												position: "relative",
											}}
										>
											{/* Modification indicator */}
											{isModified && (
												<FaiBox
													position="absolute"
													top="-8px"
													left="10px"
													bgcolor={
														isValid ? palette.primary.main : palette.error.main
													}
													color="white"
													px={1}
													py={0.5}
													borderRadius={1}
													fontSize="0.75rem"
													fontWeight="bold"
												>
													{isNewBrand ? "NEW" : "MODIFIED"}
												</FaiBox>
											)}

											{/* Missing fields indicator */}
											{isModified && !isValid && (
												<FaiBox
													position="absolute"
													top="-8px"
													right="10px"
													bgcolor={palette.warning.main}
													color="white"
													px={1}
													py={0.5}
													borderRadius={1}
													fontSize="0.75rem"
													fontWeight="bold"
												>
													{!hasName && !hasLogo
														? "MISSING NAME & LOGO"
														: !hasName
															? "MISSING NAME"
															: "MISSING LOGO"}
												</FaiBox>
											)}

											<Grid container spacing={2}>
												<Grid item xs={12} sm={6}>
													<FaiBox xs={12} sm={6}>
														<FormField
															label={`Brand Name ${index + 1}`}
															name={`brands.${index}.brandName`}
															placeholder={`Enter Brand Name ${index + 1}`}
															value={brand.brandName}
															onChange={handleBrandChange}
															onBlur={handleBlur}
															error={
																touched.brands?.[index]?.brandName &&
																Boolean(
																	(errors as BrandFormErrors).brands?.[index]
																		?.brandName
																)
															}
															helperText={
																touched.brands?.[index]?.brandName &&
																(errors as BrandFormErrors).brands?.[index]
																	?.brandName
															}
														/>
													</FaiBox>
												</Grid>

												<Grid item sm={12} xs={12}>
													<FaiBox mb={1}>
														<FaiBox
															mb={1}
															ml={0.5}
															lineHeight={0}
															display="inline-block"
														>
															<FaiTypography
																component="label"
																variant="caption"
																fontWeight="bold"
																textTransform="capitalize"
															>
																Brand Logo {index + 1}{" "}
																{!hasLogo && isModified && (
																	<span style={{ color: palette.error.main }}>
																		{" "}
																		*
																	</span>
																)}
															</FaiTypography>
														</FaiBox>

														{/* Unified Image Preview (URL or File) */}
														{categoryImages[index] ? (
															<FaiBox
																mb={2}
																position="relative"
																className="border rounded-lg h-40 w-40"
															>
																<div
																	style={{
																		position: "absolute",
																		top: "-10px",
																		right: "-10px",
																		cursor: "pointer",
																		background: "var(--theme-color)",
																		color: "white",
																		borderRadius: "50%",
																		width: "24px",
																		height: "24px",
																		display: "flex",
																		justifyContent: "center",
																		alignItems: "center",
																		boxShadow: "0 0 5px rgba(0,0,0,0.2)",
																		zIndex: 1,
																	}}
																	onClick={() => handleExistingImageRemove(index)}
																>
																	<span
																		style={{
																			fontSize: "16px",
																			fontWeight: "bold",
																		}}
																	>
																		&times;
																	</span>
																</div>
																<img
																	src={
																		typeof categoryImages[index] === "string"
																			? (categoryImages[index] as string)
																			: URL.createObjectURL(
																				categoryImages[index] as File
																			)
																	}
																	alt={`Brand Logo ${index + 1}`}
																	className="mx-auto max-h-36 mt-2"
																	style={{ objectFit: "contain" }}
																/>
															</FaiBox>
														) : (
															/* Native File Input */
															<FaiBox
																component="label"
																display="flex"
																flexDirection="column"
																alignItems="center"
																justifyContent="center"
																p={3}
																border="2px dashed #ccc"
																borderRadius={2}
																bgcolor="#f9f9f9"
																sx={{
																	cursor: "pointer",
																	"&:hover": {
																		borderColor: "#1976d2",
																		backgroundColor: "rgba(25, 118, 210, 0.04)",
																	},
																}}
															>
																<FaiTypography
																	variant="h4"
																	color="text.secondary"
																>
																	☁️
																</FaiTypography>
																<FaiTypography
																	variant="body2"
																	color="text.secondary"
																	mt={1}
																>
																	Click to upload logo
																</FaiTypography>
																<input
																	type="file"
																	accept="image/*"
																	style={{ display: "none" }}
																	onChange={(e) => {
																		if (
																			e.target.files &&
																			e.target.files.length > 0
																		) {
																			const files = Array.from(e.target.files);
																			handleImageChange(index, files);
																			// Reset value to allow re-selecting same file if needed (after delete)
																			e.target.value = "";
																		}
																	}}
																/>
															</FaiBox>
														)}
													</FaiBox>
												</Grid>

												{values.brands.length > 1 && (
													<Grid item xs={12}>
														<FaiBox
															display="flex"
															justifyContent="flex-end"
															gap={1}
														>
															{/* Show delete button for existing brands */}
															{brand.id && (
																<FaiButton
																	color="error"
																	variant="outlined"
																	size="small"
																	onClick={() => { }}
																	disabled={loading}
																>
																	Delete from Server
																</FaiButton>
															)}
															{/* Show remove button for new brands or as fallback */}
															<IconButton
																onClick={() => {
																	if (brand.id) {
																		openDeleteDialog(brand.brandName, index);
																	} else {
																		removeBrand(index);
																	}
																}}
																color="error"
															>
																<DeleteIcon />
															</IconButton>
														</FaiBox>
													</Grid>
												)}
											</Grid>
										</FaiBox>
									);
								})}

								<FaiBox mb={3}>
									<FaiButton
										color="primary"
										startIcon={<AddIcon />}
										onClick={addBrand}
										variant="outlined"
									>
										Add Another Brand
									</FaiButton>
								</FaiBox>

								{error && (
									<FaiTypography color="error" mt={2}>
										{error}
									</FaiTypography>
								)}

								{success && (
									<FaiTypography color="success" mt={2}>
										Brand details updated successfully!
									</FaiTypography>
								)}

								<FaiBox pt={"15px"}>
									<Grid container justifyContent="flex-end" spacing={2}>
										<Grid item sm={2} xs={12}>
											<FaiButton
												color="primary"
												variant={"contained"}
												fullWidth
												type="submit"
												disabled={loading}
											>
												{loading ? "Saving..." : "Next"}
											</FaiButton>
										</Grid>
									</Grid>
								</FaiBox>
							</Form>
						);
					}}
				</Formik>
			</FaiBox>

			<Dialog
				open={deleteDialogOpen}
				onClose={handleDeleteCancel}
				aria-labelledby="delete-dialog-title"
				aria-describedby="delete-dialog-description"
			>
				<DialogTitle id="delete-dialog-title">Confirm Deletion</DialogTitle>
				<DialogContent>
					<FaiTypography id="delete-dialog-description">
						Are you sure you want to delete the brand "{brandToDelete?.name}"?
						This action cannot be undone.
					</FaiTypography>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleDeleteCancel} color="secondary">
						Cancel
					</Button>
					<Button
						onClick={handleDeleteConfirm}
						color="error"
						sx={{ color: "red" }}
						variant="contained"
					>
						Delete
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}

export default BrandDetails;
